import React, { useState, useEffect } from 'react';
import { Modal, Tabs, Button, Input, Tag } from 'antd';
import type { ProgramStatusData, ProgramStatusMessage, ProgramStatusResponseMessage, HamlibControlRequestMessage, DeviceUpdateCommandMessage } from './amplifier';

interface SystemSyncModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** 与 MQTT 主题 server/device/{deviceId}/json_command 中的 deviceId 一致 */
    deviceId: string;
    /** MQTT 下发的 update_program_status 消息，用于展示 data 并组包 response */
    programStatusMessage?: ProgramStatusMessage | null;
    /** 失焦时将修改后的 data 封装为 ProgramStatusResponseMessage 通过此回调发送到 MQTT */
    onProgramStatusSave?: (message: ProgramStatusResponseMessage) => void;
    /** 固件/软件更新：cmd 为 update 的独立报文 */
    onDeviceUpdateCommand?: (message: DeviceUpdateCommandMessage) => void;
    /** Hamlib set_hamlib 消息（串口分页展示端口、波特率） */
    hamlibControlRequestMessage?: HamlibControlRequestMessage | null;
}

export const SystemSyncModal: React.FC<SystemSyncModalProps> = ({
    isOpen,
    onClose,
    deviceId,
    programStatusMessage,
    onProgramStatusSave,
    onDeviceUpdateCommand,
    hamlibControlRequestMessage
}) => {
    const programStatusData = programStatusMessage?.data;
    const hamlibData = hamlibControlRequestMessage?.data;

    const [activeTab, setActiveTab] = useState('serial');

    const [mqttIpLocal, setMqttIpLocal] = useState<string>(programStatusData?.mqtt_ip ?? '');
    const [mqttPortLocal, setMqttPortLocal] = useState<string>(programStatusData?.mqtt_port != null ? String(programStatusData.mqtt_port) : '');
    const [mqttClientIdLocal, setMqttClientIdLocal] = useState<string>(programStatusData?.mqtt_clientid ?? '');
    const [mqttUsernameLocal, setMqttUsernameLocal] = useState<string>(programStatusData?.mqtt_username ?? '');
    const [mqttPasswordLocal, setMqttPasswordLocal] = useState<string>(programStatusData?.mqtt_password ?? '');
    useEffect(() => {
        setMqttIpLocal(programStatusData?.mqtt_ip ?? '');
        setMqttPortLocal(programStatusData?.mqtt_port != null ? String(programStatusData.mqtt_port) : '');
        setMqttClientIdLocal(programStatusData?.mqtt_clientid ?? '');
        setMqttUsernameLocal(programStatusData?.mqtt_username ?? '');
        setMqttPasswordLocal(programStatusData?.mqtt_password ?? '');
    }, [programStatusData?.mqtt_ip, programStatusData?.mqtt_port, programStatusData?.mqtt_clientid, programStatusData?.mqtt_username, programStatusData?.mqtt_password]);

    const [isMqttConfigModalOpen, setIsMqttConfigModalOpen] = useState(false);
    const [mqttFormIp, setMqttFormIp] = useState('');
    const [mqttFormPort, setMqttFormPort] = useState('');
    const [mqttFormClientId, setMqttFormClientId] = useState('');
    const [mqttFormUsername, setMqttFormUsername] = useState('');
    const [mqttFormPassword, setMqttFormPassword] = useState('');
    const openMqttConfigModal = () => {
        setMqttFormIp(mqttIpLocal);
        setMqttFormPort(mqttPortLocal);
        setMqttFormClientId(mqttClientIdLocal);
        setMqttFormUsername(mqttUsernameLocal);
        setMqttFormPassword(mqttPasswordLocal);
        setIsMqttConfigModalOpen(true);
    };
    const submitMqttConfig = () => {
        const portNum = mqttFormPort.trim() === '' ? programStatusData?.mqtt_port : Number(mqttFormPort);
        const mqttPort = (typeof portNum === 'number' && !Number.isNaN(portNum)) ? portNum : (programStatusData?.mqtt_port ?? 0);
        buildProgramStatusResponse({
            mqtt_ip: mqttFormIp,
            mqtt_port: mqttPort,
            mqtt_clientid: mqttFormClientId,
            mqtt_username: mqttFormUsername,
            mqtt_password: mqttFormPassword,
        });
        setIsMqttConfigModalOpen(false);
    };

    const buildUpdateCommand = (type: 'firmware' | 'software'): DeviceUpdateCommandMessage | null => {
        if (!deviceId?.trim()) return null;
        const version =
            type === 'firmware'
                ? (programStatusData?.firmware_version ?? '')
                : (programStatusData?.software_version ?? '');
        const msgId =
            typeof globalThis.crypto?.randomUUID === 'function'
                ? globalThis.crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        return {
            msg_id: msgId,
            cmd: 'update',
            timestamp: Math.floor(Date.now() / 1000),
            data: {
                type,
                device_id: deviceId,
                device_type: 'AMP-1KW',
                version,
            },
        };
    };

    const sendDeviceUpdate = (type: 'firmware' | 'software') => {
        const msg = buildUpdateCommand(type);
        if (!msg || !onDeviceUpdateCommand) return;
        onDeviceUpdateCommand(msg);
    };

    const buildProgramStatusResponse = (
        overrides: Partial<ProgramStatusData>,
        program_action?: number,
    ) => {
        if (!programStatusMessage?.data || !onProgramStatusSave) return;
        const responseMessage: ProgramStatusResponseMessage = {
            ...programStatusMessage,
            cmd: 'update_program_status',
            timestamp: Date.now(),
            ...(program_action != null ? { program_action } : {}),
            data: {
                ...programStatusMessage.data,
                ...overrides,
            },
        };
        onProgramStatusSave(responseMessage);
    };

    return (
        <Modal
            title="系统同步"
            open={isOpen}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    取消
                </Button>,
                <Button key="confirm" type="primary" onClick={onClose}>
                    确认
                </Button>
            ]}
            width={800}
        >
            <div style={{ backgroundColor: '#1a1a1a', padding: '20px', borderRadius: '8px' }}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    style={{ marginBottom: '20px' }}
                    tabBarStyle={{
                        backgroundColor: '#2a2a2a',
                        borderBottom: '1px solid #444'
                    }}
                    items={[
                        {
                            key: 'serial',
                            label: <span style={{ color: activeTab === 'serial' ? '#fff' : '#ccc' }}>串口</span>,
                            children: (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: 'white' }}>端口:</span>
                                        <span style={{ color: 'white', minWidth: 200 }}>{hamlibData?.serial_port || '—'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: 'white' }}>波特率:</span>
                                        <span style={{ color: 'white', minWidth: 200 }}>
                                            {hamlibData?.serial_boardrate != null ? String(hamlibData.serial_boardrate) : '—'}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ color: 'white' }}>Hamlib:</span>
                                        <Tag color={programStatusData?.hamlib_connection ? 'green' : 'red'}>
                                            {programStatusData?.hamlib_connection ? '已连接' : '未连接'}
                                        </Tag>
                                    </div>
                                </div>
                            )
                        },
                        {
                            key: 'client',
                            label: <span style={{ color: activeTab === 'client' ? '#fff' : '#ccc' }}>客户端</span>,
                            children: (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: 'white' }}>硬件版本:</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <span style={{ color: 'white' }}>{programStatusData?.firmware_version ?? '—'}{(programStatusData?.firmware_updateable) ? '(Updateable)' : ''}</span>
                                            <Button style={{ backgroundColor: '#333', borderColor: '#555', color: 'white' }} onClick={() => sendDeviceUpdate('firmware')}>更新</Button>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: 'white' }}>软件版本:</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <span style={{ color: 'white' }}>{programStatusData?.software_version ?? '—'}{(programStatusData?.software_updateable) ? '(Updateable)' : ''}</span>
                                            <Button style={{ backgroundColor: '#333', borderColor: '#555', color: 'white' }} onClick={() => sendDeviceUpdate('software')}>更新</Button>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: 'white' }}>串口:</span>
                                        <span style={{ color: 'white' }}>{programStatusData?.serial_port ?? '—'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: 'white' }}>连接:</span>
                                        <Tag color={programStatusData?.serial_connection ? 'green' : 'red'}>{programStatusData?.serial_connection ? '已连接' : '未连接'}</Tag>
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                                        <button
                                            type="button"
                                            onClick={() => buildProgramStatusResponse({ mcu_reconnection: true }, 1)}
                                            style={{
                                                backgroundColor: 'rgb(51, 51, 51)',
                                                border: '1px solid rgb(85, 85, 85)',
                                                color: 'white',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                lineHeight: 1.5715,
                                                padding: '4px 15px',
                                                flex: '1 1 0%',
                                            }}
                                        >
                                            重新连接
                                        </button>
                                    </div>
                                </div>
                            )
                        },
                        {
                            key: 'server',
                            label: <span style={{ color: activeTab === 'server' ? '#fff' : '#ccc' }}>服务器</span>,
                            children: (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ color: 'white' }}>IP:</span>
                                        <span style={{ color: 'white', minWidth: 200 }}>{mqttIpLocal || '—'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ color: 'white' }}>PORT:</span>
                                        <span style={{ color: 'white', minWidth: 200 }}>{mqttPortLocal || '—'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ color: 'white' }}>ClientID:</span>
                                        <span style={{ color: 'white', minWidth: 200 }}>{mqttClientIdLocal || '—'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ color: 'white' }}>UserName:</span>
                                        <span style={{ color: 'white', minWidth: 200 }}>{mqttUsernameLocal || '—'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ color: 'white' }}>PassWd:</span>
                                        <span style={{ color: 'white', minWidth: 200 }}>{mqttPasswordLocal ? '••••••' : '—'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: 'white' }}>连接:</span>
                                        <Tag color={programStatusData?.mqtt_connection ? 'green' : 'red'}>{programStatusData?.mqtt_connection ? '已连接' : '未连接'}</Tag>
                                    </div>
                                    <Button
                                        style={{ width: '100%', marginTop: '10px', backgroundColor: '#333', borderColor: '#555', color: 'white' }}
                                        onClick={openMqttConfigModal}
                                    >
                                        配置
                                    </Button>
                                </div>
                            )
                        }
                    ]}
                />
            </div>

            <Modal
                title="MQTT 配置"
                open={isMqttConfigModalOpen}
                onCancel={() => setIsMqttConfigModalOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsMqttConfigModalOpen(false)}>取消</Button>,
                    <Button key="confirm" type="primary" onClick={submitMqttConfig}>确认</Button>,
                ]}
                width={480}
                destroyOnClose
                styles={{ body: { backgroundColor: '#1a1a1a' } }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: 'white' }}>Host / IP :</span>
                        <Input
                            value={mqttFormIp}
                            onChange={(e) => setMqttFormIp(e.target.value)}
                            style={{ width: 260, backgroundColor: '#333', borderColor: '#555', color: 'white' }}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: 'white' }}>Port :</span>
                        <Input
                            value={mqttFormPort}
                            onChange={(e) => setMqttFormPort(e.target.value)}
                            style={{ width: 260, backgroundColor: '#333', borderColor: '#555', color: 'white' }}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: 'white' }}>ClientId :</span>
                        <Input
                            value={mqttFormClientId}
                            onChange={(e) => setMqttFormClientId(e.target.value)}
                            style={{ width: 260, backgroundColor: '#333', borderColor: '#555', color: 'white' }}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: 'white' }}>Username :</span>
                        <Input
                            value={mqttFormUsername}
                            onChange={(e) => setMqttFormUsername(e.target.value)}
                            style={{ width: 260, backgroundColor: '#333', borderColor: '#555', color: 'white' }}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: 'white' }}>Password :</span>
                        <Input
                            value={mqttFormPassword}
                            onChange={(e) => setMqttFormPassword(e.target.value)}
                            style={{ width: 260, backgroundColor: '#333', borderColor: '#555', color: 'white' }}
                        />
                    </div>
                </div>
            </Modal>
        </Modal>
    );
};

export default SystemSyncModal;